import React, { View } from "react-native";
import { useTranslation } from "locales";
import {
    useThemedStyle,
    Button,
    StyleFunction,
    Headline,
} from "@venuepos/react-common";
import { ProductLayoutButtonType } from "lib";
import { Divider } from "react-native-paper";

/**
 * Show the list of possible button types and let the user choose one of them
 */
export function ButtonTypeSelector({
    hasCmpIntegration,
    onButtonTypeSelect,
}: {
    hasCmpIntegration: boolean;
    onButtonTypeSelect: (buttonType: ProductLayoutButtonType) => void;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    return (
        <View style={styles.buttons} testID="layout:add">
            {hasCmpIntegration ? (
                <Headline size="h5" style={styles.compactHeader}>
                    {t("backoffice.layout.button_types_standard", "Standard")}
                </Headline>
            ) : null}
            <View style={styles.row}>
                <View style={styles.flex}>
                    <Button
                        type="primary"
                        onPress={() => {
                            onButtonTypeSelect("PRODUCT");
                        }}
                        iconName="products"
                        testID="layout:addProduct"
                    >
                        {t(`backoffice.layout.button_type_PRODUCT`, "Product")}
                    </Button>
                </View>
                <View style={styles.flex}>
                    <Button
                        onPress={() => {
                            onButtonTypeSelect("MULTI_PRODUCT");
                        }}
                        iconName="products"
                        testID="layout:addMultiProduct"
                    >
                        {t(
                            `backoffice.layout.button_type_MULTI_PRODUCT`,
                            "Multi product"
                        )}
                    </Button>
                </View>
                <View style={styles.flex}>
                    <Button
                        onPress={() => {
                            onButtonTypeSelect("FUNCTION");
                        }}
                        iconName="cashRegister"
                        testID="layout:addFunctionButton"
                    >
                        {t(
                            `backoffice.layout.button_type_FUNCTION`,
                            "Function"
                        )}
                    </Button>
                </View>
                <View style={styles.flex}>
                    <Button
                        onPress={() => {
                            onButtonTypeSelect("LAYOUT_ENTITY");
                        }}
                        iconName="products"
                        testID="layout:layoutEntityButton"
                    >
                        {t(
                            `backoffice.layout.button_type_LAYOUT_ENTITY`,
                            "Layout entity"
                        )}
                    </Button>
                </View>
            </View>
            {hasCmpIntegration ? (
                <>
                    <Divider />
                    <Headline size="h5" style={styles.compactHeader}>
                        {t(
                            "backoffice.integration.venue_cmp.title",
                            "Venue CMP"
                        )}
                    </Headline>
                    <View style={styles.row}>
                        <View style={styles.flex}>
                            <Button
                                onPress={() => {
                                    onButtonTypeSelect("CMP_EVENT");
                                }}
                                iconName="calendarClock"
                                testID="layout:addCmpEventButton"
                            >
                                {t(
                                    `backoffice.layout.button_type_CMP_EVENT`,
                                    "CMP Event"
                                )}
                            </Button>
                        </View>
                        <View style={styles.flex}>
                            <Button
                                onPress={() => {
                                    onButtonTypeSelect("CMP_EVENT_PRODUCT");
                                }}
                                iconName="ticket"
                                testID="layout:addCmpEventProductButton"
                            >
                                {t(
                                    `backoffice.layout.button_type_CMP_EVENT_PRODUCT`,
                                    "CMP Event product"
                                )}
                            </Button>
                        </View>
                        <View style={styles.flex}>
                            <Button
                                onPress={() => {
                                    onButtonTypeSelect("CMP_PRODUCT");
                                }}
                                iconName="box"
                                testID="layout:addCmpProductButton"
                            >
                                {t(
                                    `backoffice.layout.button_type_CMP_PRODUCT`,
                                    "CMP Product"
                                )}
                            </Button>
                        </View>
                    </View>
                </>
            ) : null}
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    flex: {
        ...theme.styles.flex1,
    },
    buttons: {
        gap: theme.spacingScale * 2,
    },
    row: {
        ...theme.styles.row,
        columnGap: theme.spacingScale,
        rowGap: theme.spacingScale,
        flexWrap: "wrap",
    },

    compactHeader: { marginBottom: 0, lineHeight: 24 },
});
